import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  DeleteButton,
  TopToolbar,
  FilterButton,
  TextInput,
  SelectInput,
  FunctionField,
  useNotify,
} from 'react-admin';
import { Button, Chip, IconButton, Tooltip, Box, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { partnerMarketingAssetsFields } from '../partnerMarketingAssets.model';
import AssetUploadButton from './assetUpload.component';
import AssetBulkUploadButton from './assetBulkUpload.component';

const Empty = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '300px',
      gap: 2,
    }}
  >
    <Typography variant="h6" color="text.secondary">
      No Marketing Assets yet.
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Upload your first asset to get started.
    </Typography>
    <Box sx={{ display: 'flex', gap: 2 }}>
      <AssetBulkUploadButton />
      <AssetUploadButton />
    </Box>
  </Box>
);

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <AssetBulkUploadButton />
    <AssetUploadButton />
  </TopToolbar>
);

const assetFilters = [
  <TextInput source="search" label="Search" alwaysOn key="search" />,
  <TextInput source={partnerMarketingAssetsFields.folderPath} label="Folder" key="folder" />,
  <SelectInput
    source={partnerMarketingAssetsFields.fileType}
    label="File Type"
    key="fileType"
    choices={[
      { id: 'pdf', name: 'PDF' },
      { id: 'png', name: 'PNG' },
      { id: 'jpg', name: 'JPG' },
      { id: 'gif', name: 'GIF' },
      { id: 'webp', name: 'WebP' },
    ]}
  />,
];

const FileTypeIcon = ({ fileType }: { fileType: string }) => {
  switch (fileType) {
    case 'pdf':
      return <PictureAsPdfIcon color="error" />;
    case 'png':
    case 'jpg':
    case 'gif':
    case 'webp':
      return <ImageIcon color="primary" />;
    default:
      return <InsertDriveFileIcon />;
  }
};

const FileSizeField = ({ record }: { record?: any }) => {
  if (!record) return null;
  const bytes = record[partnerMarketingAssetsFields.fileSizeBytes];
  if (!bytes) return <span>-</span>;

  if (bytes < 1024) return <span>{bytes} B</span>;
  if (bytes < 1024 * 1024) return <span>{(bytes / 1024).toFixed(1)} KB</span>;
  return <span>{(bytes / (1024 * 1024)).toFixed(2)} MB</span>;
};

const CopyUrlButton = ({ record }: { record?: any }) => {
  const notify = useNotify();

  if (!record) return null;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(record[partnerMarketingAssetsFields.publicUrl]);
    notify('URL copied to clipboard', { type: 'success' });
  };

  return (
    <Tooltip title="Copy URL">
      <IconButton size="small" onClick={handleCopy}>
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const OpenUrlButton = ({ record }: { record?: any }) => {
  if (!record) return null;

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(record[partnerMarketingAssetsFields.publicUrl], '_blank');
  };

  return (
    <Tooltip title="Open in new tab">
      <IconButton size="small" onClick={handleOpen}>
        <OpenInNewIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const AssetsList = () => {
  return (
    <List
      title="Partner Marketing Assets"
      sort={{ field: 'created_at', order: 'DESC' }}
      actions={<ListActions />}
      filters={assetFilters}
      perPage={25}
      empty={<Empty />}
    >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <FunctionField
          label="Type"
          render={(record: any) => <FileTypeIcon fileType={record[partnerMarketingAssetsFields.fileType]} />}
        />
        <FunctionField
          label="Filename"
          source={partnerMarketingAssetsFields.displayName}
          render={(record: any) => (
            <Box>
              <Typography variant="body2">
                {record[partnerMarketingAssetsFields.displayName] || record[partnerMarketingAssetsFields.originalFilename]}
              </Typography>
              {record[partnerMarketingAssetsFields.folderPath] && (
                <Chip
                  size="small"
                  label={record[partnerMarketingAssetsFields.folderPath]}
                  variant="outlined"
                  sx={{ mt: 0.5, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          )}
        />
        <FunctionField
          label="Size"
          render={(record: any) => <FileSizeField record={record} />}
        />
        <DateField source={partnerMarketingAssetsFields.createdAt} label="Uploaded" showTime />
        <FunctionField
          label="Actions"
          render={(record: any) => (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <CopyUrlButton record={record} />
              <OpenUrlButton record={record} />
            </Box>
          )}
        />
        <DeleteButton mutationMode="pessimistic" />
      </Datagrid>
    </List>
  );
};

export default AssetsList;
